import { useMemo, useState } from 'react';

import {
  AssetDTO,
  deprecatedKinds,
  emptyAnswerFor,
  evaluateVisibility,
  FormDTO,
  FormElement,
  FormElementKind,
  FormElementUtil,
  FormRevisionDiff,
  FormType,
  getAnsweredElementIdsHiddenByChange,
  getNestingDepth,
  getTriggerFieldIds,
} from '@gearbelt/global';
import { useFormContext, useWatch } from 'react-hook-form';

import { ElementConfigSummary } from '@/components/form-revisions/element-config-summary';
import { revisionBadgeProps, revisionTintClassName } from '@/components/form-revisions/revision-decorations';
import { LogicIcon } from '@/components/icons/logic';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useConditionalLogicEnabled } from '@/hooks/use-conditional-logic-enabled';
import { cn } from '@/lib/utils';

import { FormElementComponent } from './form-element';
import { LockedFieldChangeDialog } from './locked-field-change-dialog';

export type FormRendererData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [K in FormElement['id']]: any;
};

export function FormElementsRenderer({
  form: formProp,
  asset,
  readonly = false,
  lockNonEditableElements = false,
  lockNonLatestScheduleFormEdit = false,
  isEditingExistingRecord = false,
  isPreview = false,
  elements,
  elementDecorations,
  showConfigSummary = false,
  applyVisibilityFilter = true,
}: {
  form: FormDTO;
  asset: AssetDTO;
  readonly?: boolean;
  // Lock the classifier's non-editable elements on edit (the rest stay editable); that set shrinks as cascade tickets land. Ignored when `readonly`.
  lockNonEditableElements?: boolean;
  // Lock the recalc-triggering fields (unit reading + submission-mapped completion date) beyond the classifier — a schedule-form recompute anchors on the latest check, so both edit only on the latest submission.
  lockNonLatestScheduleFormEdit?: boolean;
  // True when editing a record that already exists. The unit-reading lower guard is create-time only, matching the API's skipUnitReadingLowerCheck.
  isEditingExistingRecord?: boolean;
  isPreview?: boolean;
  elements: FormElement[];
  elementDecorations?: FormRevisionDiff;
  showConfigSummary?: boolean;
  // The revision-diff preview compares template structure with no answers, so every conditional rule
  // would resolve to hidden and drop from the diff. That surface opts out to show the full structure.
  applyVisibilityFilter?: boolean;
}) {
  const { getValues, setValue } = useFormContext<FormRendererData>();
  const [pendingTriggerChange, setPendingTriggerChange] = useState<{ applyChange: () => void; elementIdsToClear: string[] } | null>(null);
  const [isLockedFieldChangeBlocked, setIsLockedFieldChangeBlocked] = useState(false);

  // Watch the IssueSelect element's value reactively so the filter below re-evaluates
  // after the form is reset with actual repair data. Without this, getValues() returns
  // the stale default (undefined) on first render and the useMemo never re-runs.
  const issueSelectElement = useMemo(() => elements.find((e) => e.kind === FormElementKind.IssueSelect), [elements]);
  const issueSelectValue = useWatch<FormRendererData>({ name: issueSelectElement?.id ?? '', disabled: !issueSelectElement });

  // Watch the LocationSelect element's value so PassFailCheck components can pass
  // the form's selected location to issue creation (instead of stale asset.locationId).
  const locationSelectElement = useMemo(() => elements.find((e) => e.kind === FormElementKind.LocationSelect), [elements]);
  const rawLocationValue = useWatch<FormRendererData>({
    name: locationSelectElement?.id ?? '',
    disabled: !locationSelectElement,
  });
  // With no LocationSelect element the watch has no field name, so `useWatch` returns
  // the entire form-values object rather than a single field value — narrow to a
  // string so the "no location" case is `undefined`, not an empty object.
  const locationSelectValue = typeof rawLocationValue === 'string' ? rawLocationValue : undefined;

  const elementById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements]);

  // Watch only the fields other elements depend on, so a large form re-evaluates
  // visibility on a trigger answer change rather than re-rendering on every keystroke.
  const triggerFieldIds = useMemo(() => (applyVisibilityFilter ? getTriggerFieldIds(elements) : []), [applyVisibilityFilter, elements]);
  const watchedTriggerValues = useWatch<FormRendererData>({ name: triggerFieldIds, disabled: triggerFieldIds.length === 0 });

  // Conditional logic only shows/hides on form types it is enabled for; elsewhere every field renders.
  const conditionalLogicEnabled = useConditionalLogicEnabled(formProp.type);
  const triggerFieldIdSet = useMemo(() => new Set(triggerFieldIds), [triggerFieldIds]);
  const checksTriggerChanges = conditionalLogicEnabled && !readonly;

  const handleTriggerChange = (element: FormElement, nextValue: unknown, applyChange: () => void) => {
    const currentValues = getValues();
    const elementIdsToClear = getAnsweredElementIdsHiddenByChange(elements, currentValues, { ...currentValues, [element.id]: nextValue });
    if (elementIdsToClear.length === 0) {
      applyChange();
      return;
    }
    // Clearing a locked reading or completion date drops it on save (or fails the save), so the change is refused.
    const hidesLockedField =
      lockNonLatestScheduleFormEdit &&
      elementIdsToClear.some((elementId) => {
        const hiddenElement = elementById.get(elementId);
        return hiddenElement !== undefined && FormElementUtil.isFormElementRecalcTriggering(hiddenElement);
      });
    if (hidesLockedField) {
      setIsLockedFieldChangeBlocked(true);
      return;
    }
    setPendingTriggerChange({ applyChange, elementIdsToClear });
  };

  const confirmTriggerChange = () => {
    if (!pendingTriggerChange) return;
    pendingTriggerChange.applyChange();
    for (const elementId of pendingTriggerChange.elementIdsToClear) {
      const element = elementById.get(elementId);
      if (element) setValue(elementId, emptyAnswerFor(element), { shouldDirty: true });
    }
  };

  const filteredElements = useMemo(() => {
    const triggerAnswers = Object.fromEntries(triggerFieldIds.map((fieldId, index) => [fieldId, watchedTriggerValues?.[index]]));
    const visibilityByElementId = conditionalLogicEnabled && triggerFieldIds.length > 0 ? evaluateVisibility(elements, triggerAnswers) : undefined;

    return (
      elements
        .filter((element) => {
          // Filter out deprecated element kinds
          if (deprecatedKinds.includes(element.kind)) {
            return false;
          }
          // Hide fields a conditional-logic rule resolves to hidden for the current answers.
          if (visibilityByElementId?.get(element.id) === false) {
            return false;
          }
          // Hide Issue Select elements from RepairForms (issues are linked via issueId, not form elements),
          // but still show them in readonly mode if they have historical data.
          if (element.kind === FormElementKind.IssueSelect && formProp.type === FormType.RepairForm) {
            const elementValue = element.id === issueSelectElement?.id ? issueSelectValue : getValues(element.id);
            const hasHistoricalData = readonly && Array.isArray(elementValue) && elementValue.length > 0;
            if (!hasHistoricalData) {
              return false;
            }
          }
          return true;
        })
        // Depth walks each element's ancestor chain, so compute it once here rather than per render.
        .map((element) => ({ element, nestingDepth: getNestingDepth(element, elementById) }))
    );
  }, [
    elements,
    elementById,
    formProp.type,
    readonly,
    getValues,
    issueSelectValue,
    issueSelectElement,
    triggerFieldIds,
    watchedTriggerValues,
    conditionalLogicEnabled,
  ]);

  return (
    <>
      {filteredElements.map(({ element, nestingDepth }) => {
        const decoration = elementDecorations?.[element.id];
        const badge = revisionBadgeProps(decoration);
        const isRecalcTriggeringField = FormElementUtil.isFormElementRecalcTriggering(element);
        const elementReadonly =
          readonly ||
          (lockNonEditableElements && !FormElementUtil.isFormElementEditableOnRevision(element)) ||
          (lockNonLatestScheduleFormEdit && isRecalcTriggeringField);
        const lockedNotLatest = !readonly && lockNonLatestScheduleFormEdit && isRecalcTriggeringField;
        const elementContent = (
          <div id={element.id} className={cn('break-inside-avoid', decoration && 'relative', revisionTintClassName(decoration))}>
            {badge && (
              <Badge variant={badge.variant} size="sm" className={cn('absolute right-2 z-10 font-medium', badge.className)}>
                {badge.label}
              </Badge>
            )}
            <FormElementComponent
              element={element}
              readonly={elementReadonly}
              lockedNotLatest={lockedNotLatest}
              asset={asset}
              form={formProp}
              isEditingExistingRecord={isEditingExistingRecord}
              isPreview={isPreview}
              formLocationId={locationSelectValue}
              onBeforeChange={
                checksTriggerChanges && triggerFieldIdSet.has(element.id)
                  ? (nextValue, applyChange) => handleTriggerChange(element, nextValue, applyChange)
                  : undefined
              }
            />
            {showConfigSummary && <ElementConfigSummary element={element} />}
          </div>
        );

        // A conditionally-revealed field is nested under a logic glyph and a connector rail so the user sees
        // it belongs to the trigger above it. The glyph + rail are drawn once, at the field's own level; the
        // ancestor levels are plain indentation, stepping the field right so a chain reads as a hierarchy.
        if (nestingDepth === 0) {
          return <div key={element.id}>{elementContent}</div>;
        }

        let nested = (
          <div className="flex gap-5">
            <div className="flex w-5 shrink-0 flex-col items-center">
              <LogicIcon className="text-default-text shrink-0" aria-hidden />
              <div className="bg-grey-3 w-0.5 flex-1" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">{elementContent}</div>
          </div>
        );
        for (let level = 1; level < nestingDepth; level += 1) {
          nested = (
            <div className="flex gap-5">
              <div className="w-5 shrink-0" />
              <div className="min-w-0 flex-1">{nested}</div>
            </div>
          );
        }

        return (
          <div key={element.id} data-conditional-field={element.id} data-nesting-depth={nestingDepth}>
            {nested}
          </div>
        );
      })}
      <ConfirmDialog
        open={pendingTriggerChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTriggerChange(null);
        }}
        title="Clear old values?"
        description="Changing your answer will clear the values in the linked logic fields below."
        confirmVariant="success"
        onConfirm={confirmTriggerChange}
      />
      <LockedFieldChangeDialog open={isLockedFieldChangeBlocked} onOpenChange={setIsLockedFieldChangeBlocked} />
    </>
  );
}
